import type { RootState } from "@/app/store";
import { TokenTypes, userBaseURL } from "@/constants";
import type { SurveyHomepageResponse } from "@/features/home/types/home.types";
import {
  setCurrentSurvey,
  setError,
  setLoading,
  updateCurrentSurvey,
} from "@/features/survey/surveySlice";
import { GeoJson } from "@/features/survey/types/survey.mapInterface.types";
import type { DetailedSurvey } from "@survey-types/survey.core.types";
import { surveyApi } from "./base";

// Helper function to get or fetch country
const getOrFetchCountry = async (email: string): Promise<void> => {
  // Check if country is already cached in localStorage
  const cachedCountry = localStorage.getItem("country");
  if (cachedCountry) {
    return; // Country already cached, skip fetch
  }

  try {
    // Get access token from localStorage
    const accessToken = localStorage.getItem("accessToken");

    // Fetch country from API with authorization header
    const response = await fetch(
      `${userBaseURL}/user/getUserProfile/${email}`,
      {
        headers: {
          [TokenTypes.AccessToken]: accessToken || "",
        },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch user country");
    }

    const data = await response.json();
    const country = data.country;

    if (country) {
      // Save to localStorage
      localStorage.setItem("country", country);
    } else {
      localStorage.setItem("country", "United States");
    }
  } catch (error) {
    localStorage.setItem("country", "United States");
  }
};

// Survey Core API - handles basic survey operations
export const surveyCoreApi = surveyApi
  .enhanceEndpoints({
    addTagTypes: ["SurveyHomepage", "Survey"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getSurveyHomepage: build.query<SurveyHomepageResponse, void>({
        query: () => ({
          url: "/survey/surveyHomepage",
        }),
        providesTags: (result) =>
          result
            ? [
                ...result.map(({ surveyId }) => ({
                  type: "SurveyHomepage" as const,
                  id: surveyId,
                })),
                { type: "SurveyHomepage", id: "LIST" },
              ]
            : [{ type: "SurveyHomepage", id: "LIST" }],
      }),
      deleteSurvey: build.mutation<void, string>({
        query: (surveyId) => ({
          url: `/survey/${surveyId}`,
          method: "DELETE",
          responseHandler: "text", // Handle response as text
        }),
        invalidatesTags: () => ["SurveyHomepage"],
      }),
      getSurvey: build.query<DetailedSurvey, string>({
        query: (surveyId) => ({
          url: `/survey/${surveyId}`,
        }),
        providesTags: (_result, _error, surveyId) => [
          { type: "Survey", id: surveyId },
        ],
        async onQueryStarted(surveyId, { dispatch, queryFulfilled, getState }) {
          const state = getState() as unknown as RootState;
          const currentSurvey = state.survey.currentSurvey;

          // Always show loading if current survey ID doesn't match requested survey ID
          // This ensures we sync the store before showing components
          if (!currentSurvey || currentSurvey.id !== surveyId) {
            dispatch(setLoading(true));
          }

          await queryFulfilled
            .then(async ({ data }) => {
              // Get user email from localStorage
              const email = localStorage.getItem("email");

              // Fetch country if not cached
              if (email) {
                try {
                  await getOrFetchCountry(email);
                } catch (countryError) {
                  // If country fetch fails, treat it as a survey load error
                  const errorMessage = "Failed to load survey";
                  dispatch(setError(errorMessage));
                  throw countryError;
                }
              }

              // Only set survey if country fetch succeeded
              dispatch(setCurrentSurvey(data));
              dispatch(setError(null));
            })
            .catch((error) => {
              const errorObj = error?.error;
              const hasStatus =
                errorObj &&
                typeof errorObj === "object" &&
                "status" in errorObj;
              const errorMessage = hasStatus
                ? (errorObj.error ?? JSON.stringify(errorObj.data))
                : (errorObj?.message ?? "Unknown error");
              dispatch(setError(errorMessage));
            })
            .finally(() => {
              // Always clear loading state after query completes
              if (!currentSurvey || currentSurvey.id !== surveyId) {
                dispatch(setLoading(false));
              }
            });
        },
      }),
      updateSurvey: build.mutation<void, Partial<DetailedSurvey>>({
        query: (survey) => ({
          url: `/survey`,
          method: "PATCH",
          body: survey,
        }),
        async onQueryStarted(survey, { dispatch, queryFulfilled }) {
          const patches: Array<{ undo: () => void }> = [];

          if (survey.id) {
            patches.push(
              dispatch(
                surveyCoreApi.util.updateQueryData(
                  "getSurvey",
                  survey.id,
                  (draft) => {
                    Object.assign(draft, survey);
                  },
                ),
              ),
            );

            if (survey.name !== undefined) {
              patches.push(
                dispatch(
                  surveyCoreApi.util.updateQueryData(
                    "getSurveyHomepage",
                    undefined,
                    (draft) => {
                      const item = draft.find((s) => s.surveyId === survey.id);
                      if (item) item.surveyName = survey.name!;
                    },
                  ),
                ),
              );
            }
          }

          try {
            await queryFulfilled;
          } catch {
            patches.forEach((patch) => patch.undo());
          }
        },
        invalidatesTags: (_result, _error, { id }) => [
          { type: "Survey", id },
          { type: "SurveyHomepage", id },
        ],
      }),
      // Dedicated mutation for survey flag toggles (e.g. checkboxes).
      // Uses optimistic updates for both the RTK cache and the Redux store
      // but deliberately omits invalidatesTags to prevent getSurvey from
      // refetching and causing visible flickering on checkbox UI.
      updateSurveyFlags: build.mutation<void, Partial<DetailedSurvey>>({
        query: (survey) => ({
          url: `/survey`,
          method: "PATCH",
          body: survey,
        }),
        async onQueryStarted(survey, { dispatch, queryFulfilled, getState }) {
          if (!survey.id) return;

          // Snapshot the current Redux values for rollback on error
          const state = getState() as unknown as RootState;
          const currentSurvey = state.survey.currentSurvey;
          const originalValues = currentSurvey
            ? (
                Object.keys(survey).filter((k) => k !== "id") as Array<
                  keyof DetailedSurvey
                >
              ).reduce(
                (acc, key) => ({
                  ...acc,
                  [key]: currentSurvey[key],
                }),
                {} as Partial<DetailedSurvey>,
              )
            : null;

          // Optimistically update Redux store
          dispatch(updateCurrentSurvey(survey));

          // Optimistically update RTK Query cache
          const cachePatch = dispatch(
            surveyCoreApi.util.updateQueryData(
              "getSurvey",
              survey.id,
              (draft) => {
                Object.assign(draft, survey);
              },
            ),
          );

          try {
            // Backend now updates the survey timestamp for /api/survey requests
            await queryFulfilled;
          } catch {
            cachePatch.undo();
            if (originalValues) {
              dispatch(updateCurrentSurvey(originalValues));
            }
          }
        },
      }),
      createSurvey: build.mutation<DetailedSurvey, Partial<DetailedSurvey>>({
        query: (survey) => ({
          url: `/survey`,
          method: "POST",
          body: survey,
        }),
        invalidatesTags: () => [{ type: "SurveyHomepage", id: "LIST" }],
      }),
      copySurvey: build.mutation<DetailedSurvey, string>({
        query: (surveyId) => ({
          url: `/survey/copy/${surveyId}`,
          method: "GET",
        }),
        invalidatesTags: () => [{ type: "SurveyHomepage", id: "LIST" }],
      }),
      fetchGeocodeAddress: build.mutation<GeoJson, string>({
        query: (payload) => ({
          url: `/geoCode/address`,
          method: "POST",
          body: payload,
        }),
      }),
      // Reset passcode for a survey and update the store with the new one
      resetPasscode: build.mutation<{ passcode: string }, string>({
        query: (surveyId) => ({
          url: `/survey/${surveyId}/passcode/reset`,
          method: "PUT",
        }),
        async onQueryStarted(surveyId, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              updateCurrentSurvey({ id: surveyId, passcode: data.passcode }),
            );
            dispatch(
              surveyCoreApi.util.updateQueryData(
                "getSurvey",
                surveyId,
                (draft) => {
                  draft.passcode = data.passcode;
                },
              ),
            );
          } catch {
            // Ignore errors — the UI will handle them
          }
        },
      }),
      // Update survey timestamp when media or other operations occur
      updateSurveyTimestamp: build.mutation<
        { updatedOn: string },
        { surveyId: string }
      >({
        query: ({ surveyId }) => ({
          url: `/event/survey/updated/${surveyId}`,
          method: "POST",
        }),
        async onQueryStarted({ surveyId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              updateCurrentSurvey({ id: surveyId, updatedOn: data.updatedOn }),
            );
            dispatch(
              surveyCoreApi.util.updateQueryData(
                "getSurvey",
                surveyId,
                (draft) => {
                  draft.updatedOn = data.updatedOn;
                },
              ),
            );
          } catch {
            // Ignore errors — the caller handles timestamp update failures
          }
        },
        // Don't invalidate Survey tag - timestamp is just for PDF regeneration tracking
        // Prevents unnecessary survey refetches that cause map key flickering
        // Survey data will still refetch on page load, navigation, and explicit mutations
        invalidatesTags: () => [],
      }),
    }),
  });

export const {
  useGetSurveyHomepageQuery,
  useDeleteSurveyMutation,
  useGetSurveyQuery,
  useLazyGetSurveyQuery,
  useUpdateSurveyMutation,
  useUpdateSurveyFlagsMutation,
  useCreateSurveyMutation,
  useCopySurveyMutation,
  useFetchGeocodeAddressMutation,
  useUpdateSurveyTimestampMutation,
  useResetPasscodeMutation,
} = surveyCoreApi;
